import type { ReactNode } from "react"
import type { LucideIcon } from "lucide-react"

import { Card } from "@/components/ui/card"
import { cn } from "@/lib/cn"

export type StatTone = "neutral" | "accent" | "tertiary" | "success" | "warning" | "danger" | "info"

/** Shared with `CompactList`'s icon badges, so the two kit components read as one family. */
export const TONE_ICON_CLASS: Record<StatTone, string> = {
  neutral: "bg-muted text-muted-foreground",
  accent: "bg-accent/20 text-accent-foreground",
  tertiary: "bg-tertiary-subtle text-tertiary-foreground",
  success: "bg-success-subtle text-success-foreground",
  warning: "bg-warning-subtle text-warning-foreground",
  danger: "bg-destructive-subtle text-destructive-foreground",
  info: "bg-info-subtle text-info-foreground",
}

export interface StatCardProps {
  label: string
  /**
   * `"—"` is a first-class value, not an absence: pass it explicitly when
   * the real figure isn't computable yet (no dedicated count/summary source
   * for that data), and the card still renders -- muted, but present. A
   * screen that qualifies for a stat row gets the row; what it doesn't get
   * is an invented number standing in for one the API can't give it yet.
   */
  value: ReactNode
  icon?: LucideIcon
  tone?: StatTone
  hint?: ReactNode
  selected?: boolean
  onClick?: () => void
}

/**
 * A single at-a-glance figure for the top of a list screen: uppercase
 * label and tinted icon badge on one line, the big number below, a one-line
 * hint under that. No period-over-period comparison (that is the kit's
 * `SummaryCards`, LLD §8.1): this is a screen's own live count, always
 * backed by data the screen already fetches (or an explicit `"—"` when it
 * isn't), never an invented figure. `onClick` makes the card double as a
 * filter shortcut, with a keyboard-reachable button role rather than a bare
 * clickable div.
 */
export function StatCard({ label, value, icon: Icon, tone = "neutral", hint, selected, onClick }: StatCardProps) {
  const interactive = Boolean(onClick)
  const isPlaceholder = value === "—"

  return (
    <Card
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                onClick?.()
              }
            }
          : undefined
      }
      data-selected={selected}
      data-stat-card=""
      className={cn(
        "min-w-48 flex-1 basis-48 gap-2 border-border/70 px-4 py-3.5 transition-all",
        interactive &&
          "cursor-pointer hover:-translate-y-0.5 hover:border-border hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        selected && "border-ring ring-2 ring-ring ring-offset-2 ring-offset-background",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="min-w-0 text-xs font-medium tracking-wide text-pretty break-words text-muted-foreground uppercase">{label}</p>
        {Icon ? (
          <div
            className={cn(
              "flex size-9 shrink-0 items-center justify-center rounded-lg",
              TONE_ICON_CLASS[tone],
            )}
          >
            <Icon className="size-4" aria-hidden />
          </div>
        ) : null}
      </div>
      <p
        className={cn(
          "numeric text-2xl leading-tight font-semibold",
          isPlaceholder ? "text-muted-foreground" : "text-foreground",
        )}
      >
        {value}
      </p>
      {hint ? <p className="truncate text-xs text-muted-foreground">{hint}</p> : null}
    </Card>
  )
}

/**
 * The standard responsive row every screen's stat cards share. Flex-wrap
 * with each `StatCard` carrying its own `flex-1 basis-48`, not a fixed
 * `grid-cols-4` -- a grid with N fixed tracks divides a 2-card row into the
 * same narrow columns it would for 4, truncating longer labels for no
 * reason; flex-wrap lets 1-3 cards fill the row's real width and only wraps
 * once there genuinely isn't room.
 */
export function StatCardGrid({ children }: { children: ReactNode }) {
  return <div className="mb-5 flex flex-wrap gap-3">{children}</div>
}
